'use client';
import React, { useState } from 'react';
import Modal from '@/components/Modal';
import { productDetailsData } from '../data/productDetails';
import { motion } from 'framer-motion';
import Image from 'next/image';

// Interface defined within the component file as it's only used here
interface ProductProps {
  title: string;
  descriptionLines: string[];
}

// Renamed function to ProductGrid and export it
export default function ProductGrid() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState<{
    title: string;
    content: React.ReactNode;
  } | null>(null);

  const products: ProductProps[] = [
    {
      title: 'DJ-SALES',
      descriptionLines: [
        'Segmentation brought into the 21st century.',
        "Use the HCP's digital footprint to prioritize high-potential customers previously below the radar and speed up launch success in new markets.",
      ],
    },
    {
      title: 'DJ-INFLUENCE',
      descriptionLines: [
        'Who are the big influencers in your market?',
        'Identify your digital opinion leaders.',
      ],
    },
    {
      title: 'DJ-CHANNEL',
      descriptionLines: [
        'Identify the preferred communication channels for each HCP (Face-to-Face | Video chat | Phone | Email).',
        'Supercharge your Omnichannel strategy.',
      ],
    },
    {
      title: 'DJ-PERSONA',
      descriptionLines: [
        "PERSONA is a customised profile of relevant archetypes that aligns with & enrichs Client's Market Research segments.",
        'Each Persona provides a multifaceted and actionable view of the customer.',
      ],
    },
    {
      title: 'DJ-ADOPTER',
      descriptionLines: [
        'Some HCPs love the new, some prefer to "wait and see".',
        'Identify your early adopters and get rapid traction in the market.',
      ],
    },
    {
      title: 'DJ-MIND',
      descriptionLines: [
        "Understand your HCPs' individual behaviours and preferences.",
        '4 personality types: Manager, Inspiring, Nourishing, Detailed.',
        'Build individually customized communication.',
      ],
    },
    {
      title: 'DJ-FOCUS',
      descriptionLines: [
        'What is the highest focus for each HCP? (Business | Academic | Education)',
        'Know their focus and start building programmes to support them.',
      ],
    },
    {
      title: 'DJ-AFFINITY',
      descriptionLines: [
        'Which HCPs are open to your technology?',
        'Measure individual interest in specific topics, therapy types or disease states.',
      ],
    },
    {
      title: 'DJ-NETWORK',
      descriptionLines: [
        'Are your HCPs active in social networks? (Facebook | Twitter | LinkedIn | Instagram | Researchgate)',
        'Build your digital campaigns knowing exactly which HCPs you are reaching.',
      ],
    },
    {
      title: 'DJ-VIBE',
      descriptionLines: [
        'Emotions are the core of decision making.',
        'Go deeper than common social listening and tap into the emotion of your market.',
      ],
    },
    {
      title: 'DJ-ALERT',
      descriptionLines: [
        "Don't miss critical changes in your market.",
        'Keep your finger on the pulse and fine-tune your sales and marketing strategies.',
      ],
    },
    {
      title: 'DJ-BOOST',
      descriptionLines: [
        'Is your sales curve starting to flatten?',
        'Add a 2nd dimension to your targeting.',
        'Find HCPs not yet prescribing.',
      ],
    },
  ];

  // NOTE: Service schema generation is removed here, will be in page.tsx

  const openModal = (product: ProductProps) => {
    const productId = product.title.toLowerCase();
    const details = productDetailsData.find((detail) => detail.id === productId);

    if (details) {
      setModalContent({
        title: details.modalTitle,
        content: details.modalContent,
      });
      setIsModalOpen(true);
    } else {
      console.log('Clicked on product without defined modal details:', product.title);
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setModalContent(null);
  };

  return (
    // NOTE: Removed the outer fragment and schema script
    <div className="min-h-screen bg-gray-900 text-gray-100">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        <h1 className="text-4xl md:text-5xl font-bold text-center mb-4 text-transparent bg-clip-text bg-gradient-to-r from-[#59b9c1] to-[#59b9c1]">
          Products
        </h1>
        <p className="text-center text-gray-400 mb-12 text-lg">
          Healthcare insights at the doctor level
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {products.map((product) => (
            <button
              key={product.title}
              onClick={() => openModal(product)}
              className="text-left focus:outline-none focus:ring-2 focus:ring-[#59b9c1] focus:ring-offset-2 focus:ring-offset-gray-900 rounded-lg h-full"
              aria-label={`View details for ${product.title}`}
            >
              <ModernProductCard {...product} />
            </button>
          ))}
        </div>
      </div>

      {isModalOpen && modalContent && (
        <Modal isOpen={isModalOpen} onClose={closeModal} title={modalContent.title}>
          {modalContent.content}
        </Modal>
      )}
    </div>
  );
}

// Card component remains here as it's specific to this grid display
const ModernProductCard: React.FC<ProductProps> = ({ title, descriptionLines }) => (
  <motion.div
    className="group relative bg-gray-800 rounded-lg overflow-hidden shadow-lg transition-all duration-300 ease-in-out h-full flex flex-col cursor-pointer"
    whileHover={{
      scale: 1.03,
      boxShadow: '0 25px 50px -12px rgb(0 0 0 / 0.4)',
    }}
    transition={{ duration: 0.2, ease: 'easeInOut' }}
  >
    <div className="relative w-full h-48 overflow-hidden flex-shrink-0">
      <Image
        fill
        src={`/images/${title}.jpg`}
        alt={`Illustration representing ${title} for ${descriptionLines[0].toLowerCase()}`}
        className="object-cover transition-transform duration-300 group-hover:scale-110"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-gray-800 via-transparent to-transparent opacity-50"></div>
    </div>

    <div className="p-5 flex-grow flex flex-col justify-between">
      <div>
        <h3 className="text-xl font-bold text-[#59b9c1] mb-2 truncate">{title}</h3>
        <div className="text-gray-300 text-sm space-y-1">
          {descriptionLines.map((line, index) => (
            <p key={index} className="line-clamp-2">
              {line}
            </p>
          ))}
        </div>
      </div>
    </div>
    <div className="absolute bottom-0 left-0 h-1 w-full bg-[#59b9c1] scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></div>
  </motion.div>
);
